import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import BlockPlaceholder from 'components/consumer/BlockPlaceholder';
import JBImage from 'components/essentials/JBImage';
import HotSpotWrapper from 'components/consumer/hotspot-wrapper';
import browserStorage from 'components/consumer/browser-storage';
const getVerticalScrollPosition = () => window.pageYOffset;
const getPhotoViewportWidth = (isMobile, mobileItemsPerRow) => {
  if (!isMobile) return '25vw';
  if (mobileItemsPerRow === 3) return '33vw';
  if (mobileItemsPerRow === 2) return '50vw';
  return '100vw';
};
const MasonryPhotoCard = ({
  active,
  filterActions,
  hits,
  isMobile,
  mobileItemsPerRow,
  noScrollToView,
  photo,
  searchPhotoId,
  selectedGridItem,
  setActiveId,
  updateSelectedGridItem
}) => {
  const componentRef = useRef();
  const location = useLocation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const height = useRef(`${20 + Math.floor(Math.random() * 20)}vh`);
  const handleImageLoaded = useCallback(() => {
    setImageLoaded(true);
  }, []);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);
  const handleMouseLeave = useCallback(e => {
    setIsHovered(false);
    if (componentRef.current && e.relatedTarget?.closest && !e.relatedTarget.closest('.hotspot-tooltip-wrapper')) {
      ReactTooltip.hide();
      setActiveId(null);
    }
  }, [setActiveId]);
  const handleClick = useCallback(() => {
    if (updateSelectedGridItem) {
      if (location.pathname !== '/free-design-services/design-quiz') {
        filterActions?.onImageSelect(photo.photo_id);
      }
      updateSelectedGridItem(photo, hits);
      if (!isMobile && !noScrollToView) {
        setTimeout(() => {
          if (componentRef.current) {
            componentRef.current.scrollIntoView({
              behavior: 'smooth'
            });
          }
          browserStorage().setItem('scrollPosition.customerPhotosPage', getVerticalScrollPosition());
        }, 0);
      }
    }
  }, [updateSelectedGridItem, location.pathname, filterActions, photo, hits, isMobile, noScrollToView]);
  if (!photo) {
    return <div className="w-full min-h-[20vh] text-base text-[#5c5c5c] flex items-center justify-center">
        No product found!
      </div>;
  }
  const isSelected = selectedGridItem && selectedGridItem.photo_id === photo.photo_id || searchPhotoId === photo.photo_id;
  const imgHeight = !isMobile && !imageLoaded ? height.current : undefined;
  const photoElement = <JBImage sizes={getPhotoViewportWidth(isMobile, mobileItemsPerRow)} onMouseEnter={() => {
    if (!active) {
      ReactTooltip.hide();
      setActiveId(photo.photo_id);
    }
  }} alt="" src={photo.image_link} trim="auto" onLoad={handleImageLoaded} className={cx({
    'max-[767px]:h-auto': mobileItemsPerRow === 1,
    'max-[767px]:absolute max-[767px]:inset-0': mobileItemsPerRow === 2 || 3
  })} />;
  return <button type="button" data-xc="customer-photo-card" className={cx('customer-photo-card__image', 'w-full md:w-full lg:w-full', 'relative [transition:transform_.25s_ease-in-out] md:p-4', '[&_img]:cursor-pointer [&_img]:align-middle [&_img]:w-full [&_img]:opacity-0 [&_img]:[transition:opacity_.3s_ease-in-out] [&_img]:max-[767px]:object-cover [&_img]:max-[767px]:h-full [&_img]:md:h-auto [&_img]:md:rounded', 'max-[767px]:before:[content:"_"] max-[767px]:before:block max-[767px]:before:w-full max-[767px]:before:pt-[100%]', 'max-[767px]:[&_img]:bottom-0 max-[767px]:[&_img]:left-0 max-[767px]:[&_img]:absolute max-[767px]:[&_img]:right-0 max-[767px]:[&_img]:top-0', {
    '[&_img]:shadow-[0_0_0_3px_#fff,0_0_0_5px_#262626]': isSelected,
    'hover:md:scale-105 hover:min-[1600px]:transform-[scale(1.03)]': isHovered,
    'max-[767px]:relative before:max-[767px]:[content:"_"] before:max-[767px]:block before:max-[767px]:w-full before:max-[767px]:pt-[100%]': mobileItemsPerRow === 2 || 3
  })} ref={componentRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick} style={{
    height: imgHeight
  }}>
      {!imageLoaded && <BlockPlaceholder className="!w-full !h-full !rounded-[2%]" />}
      {!isMobile ? <HotSpotWrapper showTooltip showOnActive isActive={active} src={photo.image_link} style={{
      width: '100%',
      height: '100%'
    }}>
          {photoElement}
        </HotSpotWrapper> : <HotSpotWrapper dontFadeTagIcon showTooltip showOnActive fromMobileGrid isActive={active} src={photo.image_link} style={{
      width: '100%',
      height: '100%'
    }}>
          {photoElement}
        </HotSpotWrapper>}

      {isMobile && mobileItemsPerRow === 1 && <div className="p-4 mb-8">
          <div className="text-base font-bold">
            {`${photo.customer_first_name} ${photo.customer_last_name && photo.customer_last_name[0]}`}
          </div>
          <div className="text-sm text-gray">{photo.photo_comment}</div>
        </div>}
    </button>;
};
MasonryPhotoCard.propTypes = {
  active: PropTypes.bool,
  filterActions: PropTypes.shape({
    onImageSelect: PropTypes.func
  }),
  hits: PropTypes.array,
  isMobile: PropTypes.bool,
  mobileItemsPerRow: PropTypes.number,
  noScrollToView: PropTypes.bool,
  photo: PropTypes.shape({
    customer_first_name: PropTypes.string,
    customer_last_name: PropTypes.string,
    image_link: PropTypes.string,
    photo_comment: PropTypes.string,
    photo_id: PropTypes.number
  }),
  searchPhotoId: PropTypes.string,
  selectedGridItem: PropTypes.shape({
    photo_id: PropTypes.number
  }),
  setActiveId: PropTypes.func,
  updateSelectedGridItem: PropTypes.func
};
export default MasonryPhotoCard;